/**
 * RecordButton - Presentational recording button with progress indicator
 * 
 * This is a "dumb" component that receives all state as props.
 * Use with useRecording hook for state management.
 * 
 * Features:
 * - shrink={true}: Button shrinks when recording (WebXR style)
 * - shrink={false}: Button stays same size (standard style)
 * - Processing/encoding spinner
 * 
 * Requires Tailwind CSS.
 */

export interface RecordButtonProps {
  /** Whether currently recording */
  isRecording?: boolean;
  /** Whether processing/encoding video */
  isProcessing?: boolean;
  /** Recording progress (0-100) */
  progress?: number;
  /** Called when button is clicked */
  onClick?: () => void;
  /** Shrink button when recording (default: false) */
  shrink?: boolean;
  /** Button size in pixels (default: 90 for standard, 64 for shrink mode) */
  size?: number;
  /** Custom className for the container */
  className?: string;
  /** Disable the button */
  disabled?: boolean;
}

// Shrink mode constants (shrink=true)
const SHRINK_VIEWBOX = 100;
const SHRINK_RADIUS = 47;
const SHRINK_CENTER = 50;
const SHRINK_CIRCUMFERENCE = 2 * Math.PI * SHRINK_RADIUS;

// Standard mode constants (shrink=false)
const STANDARD_VIEWBOX = 38;
const STANDARD_RADIUS = 16;
const STANDARD_CENTER = 19;
const STANDARD_CIRCUMFERENCE = 100.5;

export function RecordButton({ 
  isRecording = false,
  isProcessing = false,
  progress = 0,
  onClick,
  shrink = false,
  size: sizeProp,
  className = '',
  disabled = false,
}: RecordButtonProps) {
  const size = sizeProp ?? (shrink ? 64 : 90);
  const isDisabled = disabled || isProcessing;

  // ============================================================================
  // SHRINK MODE (WebXR style)
  // ============================================================================
  if (shrink) {
    const buttonSize = isRecording ? size * 0.5 : size;
    const innerSize = size * 0.38;
    const innerRecordingSize = size * 0.2;
    const strokeDashoffset = SHRINK_CIRCUMFERENCE - (SHRINK_CIRCUMFERENCE * progress) / 100;

    return (
      <div 
        className={`relative flex items-center justify-center pointer-events-auto ${className}`}
        style={{ width: size, height: size }}
      >
        <button
          onClick={onClick}
          disabled={isDisabled}
          className="relative flex items-center justify-center rounded-full border-2 border-white bg-black/30 backdrop-blur-sm transition-all duration-200 ease-out disabled:opacity-60 disabled:cursor-not-allowed active:scale-95"
          style={{ width: buttonSize, height: buttonSize, touchAction: 'manipulation' }}
          aria-label={isRecording ? 'Stop recording' : isProcessing ? 'Processing video' : 'Start recording'}
        >
          {/* Inner shape - circle when idle, square when recording */}
          <div
            className={`bg-red-500 transition-all duration-200 ease-out ${isRecording ? 'rounded-sm' : 'rounded-full'}`}
            style={{
              width: isRecording ? innerRecordingSize : innerSize,
              height: isRecording ? innerRecordingSize : innerSize,
            }}
          />

          {/* Progress ring - scales outside the shrunken button */}
          {isRecording && (
            <svg 
              className="absolute inset-0 pointer-events-none"
              style={{ width: '100%', height: '100%', transform: 'scale(1.35)' }}
              viewBox={`0 0 ${SHRINK_VIEWBOX} ${SHRINK_VIEWBOX}`}
            >
              <circle
                cx={SHRINK_CENTER}
                cy={SHRINK_CENTER}
                r={SHRINK_RADIUS}
                fill="none"
                stroke="#ef4444"
                strokeWidth="8"
                strokeLinecap="round"
                strokeDasharray={SHRINK_CIRCUMFERENCE}
                strokeDashoffset={strokeDashoffset}
                style={{ transform: 'rotate(-90deg)', transformOrigin: 'center', transition: 'stroke-dashoffset 0.1s linear' }}
              />
            </svg>
          )}

          {/* Processing spinner */}
          {isProcessing && (
            <svg className="absolute inset-0 w-full h-full animate-spin" viewBox="0 0 100 100">
              <circle cx="50" cy="50" r="42" fill="none" stroke="#ef4444" strokeWidth="4" strokeDasharray="66 198" strokeLinecap="round" />
            </svg>
          )}
        </button>
      </div>
    );
  }

  // ============================================================================
  // STANDARD MODE (shrink=false)
  // ============================================================================
  const standardStrokeDashoffset = isRecording 
    ? STANDARD_CIRCUMFERENCE - (progress * STANDARD_CIRCUMFERENCE) / 100 
    : STANDARD_CIRCUMFERENCE;

  return (
    <button 
      className={`relative transition-opacity duration-500 ease-in-out pointer-events-auto cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed ${className}`}
      style={{ width: size, height: size, touchAction: 'manipulation' }}
      onClick={onClick}
      disabled={isDisabled}
      aria-label={isRecording ? 'Stop recording' : isProcessing ? 'Processing video' : 'Start recording'}
    >
      {/* SVG with outer track and progress ring */}
      <svg
        viewBox={`0 0 ${STANDARD_VIEWBOX} ${STANDARD_VIEWBOX}`}
        className={`block absolute top-0 left-0 w-full h-full pointer-events-none ${isRecording ? 'opacity-100' : 'transition-all duration-300'}`}
      >
        {/* Outer circle track */}
        <circle
          className="fill-black/20 stroke-white transition-all duration-700"
          style={{ strokeWidth: 1 }}
          r={STANDARD_RADIUS}
          cx={STANDARD_CENTER}
          cy={STANDARD_CENTER}
        />
        
        {/* Progress bar */}
        <circle
          className={`fill-transparent transition-none origin-center ${isRecording ? 'opacity-100' : 'opacity-0'}`}
          style={{
            stroke: 'rgba(255, 52, 52, 0.5)',
            strokeWidth: 6,
            strokeDasharray: `${STANDARD_CIRCUMFERENCE} ${STANDARD_CIRCUMFERENCE}`,
            strokeDashoffset: standardStrokeDashoffset,
            transform: 'rotate(-90deg) scale(0.67)',
            transformOrigin: 'center',
          }}
          r={STANDARD_RADIUS}
          cx={STANDARD_CENTER}
          cy={STANDARD_CENTER}
        />
      </svg>

      {/* Processing spinner */}
      {isProcessing && (
        <svg className="absolute inset-0 w-full h-full animate-spin pointer-events-none" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r="42" fill="none" stroke="#ef4444" strokeWidth="4" strokeDasharray="66 198" strokeLinecap="round" />
        </svg>
      )}

      {/* Inner red dot - scales slightly when recording */}
      <div
        className={`absolute top-0 left-0 origin-center bg-[#FF3434] transition-all duration-300 w-full h-full rounded-full pointer-events-none ${
          isRecording ? 'scale-[0.48]' : 'scale-[0.35]'
        }`}
      />
    </button>
  );
}

export default RecordButton;
